import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxChip, OnyxOptional } from '@onyx/angular';
import {
  OnyxChipsComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxModalComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { isString, without } from 'lodash';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { ModalNoteComponent } from '../../../common/components/modals/modal-note/modal-note.component';
import {
  ROUTE_CONTEXT,
  RouteService,
} from '../../../common/services/route.service';
import { ModalsStore } from '../../../common/stores/modals.store';
import { OrderPlanningMode } from '../common/enums/order-planning-mode';
import { OrderStatus } from '../common/enums/order-status';
import { OrdersRoute } from '../common/enums/orders-route';
import {
  ORDER_HEADER_ACTIONS,
  OrderHelper,
} from '../common/helpers/order.helper';
import type { Order, SimplifiedOrder } from '../common/interfaces/order';
import { OrdersService } from '../common/services/orders.service';
import { OrderModalHeaderDescriptionComponent } from './order-modal-header-description/order-modal-header-description.component';
import { OrderModalDetailsComponent } from './order-modal-sections/order-modal-details/order-modal-details.component';
import { OrderModalDocumentsComponent } from './order-modal-sections/order-modal-documents/order-modal-documents.component';
import { OrderModalRouteComponent } from './order-modal-sections/order-modal-route/order-modal-route.component';
import { OrderModalSummaryComponent } from './order-modal-sections/order-modal-summary/order-modal-summary.component';
import { OrderModalStatusesComponent } from './order-modal-statuses/order-modal-statuses.component';

export type OrderModalData = {
  order: Order | SimplifiedOrder | string;
  planningMode?: OnyxOptional<OrderPlanningMode>;
};

enum OrderModalSection {
  SUMMARY,
  ROUTE,
  DOCUMENTS,
  DETAILS,
  NOTE,
}

@Component({
  selector: 'app-order-modal',
  imports: [
    OnyxModalComponent,
    OnyxIconButtonComponent,
    OnyxDropdownDirective,
    OnyxTooltipDirective,
    OnyxIconComponent,
    TranslatePipe,
    OnyxChipsComponent,
    OrderModalDocumentsComponent,
    OrderModalDetailsComponent,
    ModalNoteComponent,
    OrderModalStatusesComponent,
    OrderModalSummaryComponent,
    forwardRef(() => OrderModalRouteComponent),
    OrderModalHeaderDescriptionComponent,
  ],
  providers: [{ provide: ROUTE_CONTEXT, useValue: 'order' }, RouteService],
  templateUrl: './order-modal.component.html',
  styleUrl: './order-modal.component.scss',
})
export class OrderModalComponent {
  protected readonly data = inject<OrderModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ordersService = inject(OrdersService);
  private readonly orderHelper = inject(OrderHelper);
  private readonly modalsStore = inject(ModalsStore);

  protected readonly I18N = 'orders.orderModal';

  protected readonly OrdersRoute = OrdersRoute;
  protected readonly OrderHelper = OrderHelper;
  protected readonly OrderModalSection = OrderModalSection;
  protected readonly OrderPlanningMode = OrderPlanningMode;
  protected readonly OrderStatus = OrderStatus;

  private readonly uuid: string;

  protected order = signal<Order | null>(null);

  protected section = this.modalsStore.getActiveSection(
    OrderModalComponent.name,
    () => this.sections(),
  );
  protected loading = signal(false);
  protected error = signal(false);
  protected order$ = new Subject<void>();
  protected close$ = new Subject<void>();

  protected options = computed(() =>
    this.order()
      ? this.orderHelper.getOptions(
          this.order()!,
          { close$: this.close$ },
          this.hasShowOnMap() && !this.isInactive()
            ? ORDER_HEADER_ACTIONS
            : without(ORDER_HEADER_ACTIONS, 'showOnGantt'),
        )
      : null,
  );
  protected hasEdit = computed(() =>
    this.order() ? OrderHelper.hasEdit(this.order()!) : false,
  );
  protected isInactive = computed(() =>
    this.order() ? OrderHelper.isInactive(this.order()!) : false,
  );
  protected sections = computed<OnyxChip<OrderModalSection>[]>(() => [
    { name: 'labels.summary', value: OrderModalSection.SUMMARY },
    { name: 'labels.route', value: OrderModalSection.ROUTE },
    {
      name: 'labels.documents',
      value: OrderModalSection.DOCUMENTS,
      count: this.order()
        ? this.order()!.documents.length +
          (this.order()!.payment?.invoice?.scan ? 1 : 0)
        : undefined,
    },
    { name: 'labels.details', value: OrderModalSection.DETAILS },
    { name: 'labels.note', value: OrderModalSection.NOTE },
  ]);

  protected orderVehicle = computed(() =>
    this.order() ? OrderHelper.getOrderVehicle(this.order()!) : null,
  );
  protected hasShowOnMap = computed(() =>
    this.order() ? OrderHelper.hasShowOnMap(this.order()!) : false,
  );

  constructor() {
    const order = this.data.order;
    this.uuid = isString(order) ? order : order.uuid;

    this.order$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.ordersService.getOrder(this.uuid).pipe(
            catchError(() => {
              this.order.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((order) => {
        this.order.set(order);
        this.loading.set(false);
        this.error.set(false);
      });

    if (isString(order) || !OrderHelper.isOrderType(order)) {
      this.order$.next();
    } else {
      this.order.set(order);
    }

    this.ordersService.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.order$.next());
  }

  protected edit(): void {
    this.orderHelper.edit(this.uuid, {
      options: { close$: this.close$ },
    });
  }

  protected share(): void {
    const order = this.order();
    if (order) this.orderHelper.share(order);
  }

  protected showOnMap(): void {
    const order = this.order();
    if (!order) return;

    this.orderHelper.showOnMap(order, { close$: this.close$ });
  }

  protected showOnGantt(): void {
    const order = this.order();
    if (!order) return;

    this.orderHelper.showOnGantt(order, { close$: this.close$ });
  }
}
